import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../services/api";
import { Plus, Trash2, Save, ArrowLeft, Plane } from "lucide-react";

const AIRCRAFT = ["Helicopter", "Aircraft", "Airliner"];
const CONDITIONS = ["NE", "NS", "OH", "SV", "RE", "AS"];
const emptyLine = () => ({
    part_number: "",
    alternate_part_number: "",
    nsn: "",
    aircraft_model: "",
    acceptable_conditions: [],
    quantity: 1,
    requested_lead_time: "",
});

const NewRfq = () => {
    const navigate = useNavigate();
    const [aircraftType, setAircraftType] = useState("Aircraft");
    const [notes, setNotes] = useState("");
    const [lines, setLines] = useState([emptyLine()]);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const update = (i, field, value) =>
        setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));

    const addLine = () => setLines((ls) => [...ls, emptyLine()]);
    const removeLine = (i) => setLines((ls) => (ls.length === 1 ? ls : ls.filter((_, idx) => idx !== i)));

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        // Validate
        const sanitized = lines.map((l) => ({
            ...l,
            part_number: l.part_number.trim(),
            alternate_part_number: l.alternate_part_number.trim(),
            nsn: l.nsn.trim(),
            aircraft_model: l.aircraft_model.trim(),
            acceptable_conditions: l.acceptable_conditions || [],
            requested_lead_time: l.requested_lead_time.trim(),
            quantity: Number(l.quantity) || 0,
        }));
        const invalid = sanitized.findIndex((l) => !l.part_number || l.quantity < 1);
        if (invalid >= 0) {
            setErr(`Line ${invalid + 1}: Part Number and Quantity (≥1) are required.`);
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/rfq", {
                aircraft_type: aircraftType,
                items: sanitized,
                notes,
            });
            navigate(`/portal/rfqs/${data.id}`, { replace: true });
        } catch (e2) {
            setErr(formatApiError(e2));
            setBusy(false);
        }
    };

    return (
        <div data-testid="new-rfq-page">
            <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 font-body text-sm mb-6 hover:underline"
                style={{ color: "var(--ns-muted)" }}
            >
                <ArrowLeft size={14} /> Back
            </button>

            <div className="mb-10">
                <div
                    className="font-body text-[11px] uppercase tracking-[0.22em] mb-2"
                    style={{ color: "var(--ns-muted)" }}
                >
                    Request For Quotation
                </div>
                <h1
                    className="font-display text-3xl lg:text-4xl font-bold tracking-tight"
                    style={{ color: "var(--ns-ink)" }}
                >
                    New RFQ
                </h1>
            </div>

            {err && (
                <div className="mb-6 px-4 py-3 border bg-[#fff3f1] border-[#e6c8c2] font-body text-sm" style={{ color: "#7a2218" }} data-testid="newrfq-error">
                    {err}
                </div>
            )}

            <form onSubmit={onSubmit} className="space-y-10">
                {/* General Info */}
                <section className="bg-white border border-[var(--ns-border)] p-7 lg:p-8">
                    <h2 className="font-display text-sm font-semibold uppercase tracking-[0.18em] mb-6" style={{ color: "var(--ns-ink)" }}>
                        General Information
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                            <label className="block font-body text-[11px] uppercase tracking-[0.22em] mb-3" style={{ color: "var(--ns-muted)" }}>
                                Aircraft Type *
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {AIRCRAFT.map((a) => {
                                    const active = aircraftType === a;
                                    return (
                                        <button
                                            key={a}
                                            type="button"
                                            onClick={() => setAircraftType(a)}
                                            data-testid={`aircraft-${a.toLowerCase()}`}
                                            className={`inline-flex items-center gap-2 px-4 py-3 font-body text-sm border transition-colors ${
                                                active
                                                    ? "bg-[var(--ns-ink)] text-white border-[var(--ns-ink)]"
                                                    : "bg-white border-[var(--ns-border)] hover:border-[var(--ns-ink)]"
                                            }`}
                                            style={!active ? { color: "var(--ns-body)" } : {}}
                                        >
                                            <Plane size={14} />
                                            {a}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div>
                            <label className="block font-body text-[11px] uppercase tracking-[0.22em] mb-3" style={{ color: "var(--ns-muted)" }}>
                                Notes / Remarks
                            </label>
                            <input
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Optional"
                                className="w-full bg-white border border-[var(--ns-border)] px-4 py-3 font-body text-sm focus:outline-none focus:border-[var(--ns-ink)] transition-colors"
                                style={{ color: "var(--ns-ink)" }}
                            />
                        </div>
                    </div>
                </section>

                {/* Material lines */}
                <section className="bg-white border border-[var(--ns-border)]">
                    <div className="flex items-center justify-between px-6 lg:px-8 py-5 border-b border-[var(--ns-border)]">
                        <h2 className="font-display text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: "var(--ns-ink)" }}>
                            Material Request Lines
                        </h2>
                        <button
                            type="button"
                            onClick={addLine}
                            className="ns-btn-ghost"
                            data-testid="add-material-line"
                        >
                            <Plus size={14} /> Add Material Line
                        </button>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-[var(--ns-border)]" style={{ background: "#fafaf8" }}>
                                    <Th w="60px">#</Th>
                                    <Th>Part Number *</Th>
                                    <Th>Alternate P/N</Th>
                                    <Th>NSN</Th>
                                    <Th>Aircraft Model</Th>
                                    <Th w="220px">Acceptable Conditions</Th>
                                    <Th w="110px">Quantity *</Th>
                                    <Th>Requested Lead Time</Th>
                                    <Th w="60px"></Th>
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map((l, i) => (
                                    <tr key={i} className="border-b border-[var(--ns-border)] last:border-0" data-testid={`line-${i}`}>
                                        <td className="px-4 py-3 text-center font-body text-sm tabular-nums" style={{ color: "var(--ns-muted)" }}>
                                            {String(i + 1).padStart(2, "0")}
                                        </td>
                                        <CellInput value={l.part_number} onChange={(v) => update(i, "part_number", v)} placeholder="e.g. 332A32-3611-01" required testid={`line-${i}-pn`} />
                                        <CellInput value={l.alternate_part_number} onChange={(v) => update(i, "alternate_part_number", v)} placeholder="—" />
                                        <CellInput value={l.nsn} onChange={(v) => update(i, "nsn", v)} placeholder="—" />
                                        <CellInput value={l.aircraft_model} onChange={(v) => update(i, "aircraft_model", v)} placeholder="e.g. AW139, B737-800" testid={`line-${i}-acmodel`} />
                                        <td className="px-3 py-2">
                                            <div className="flex flex-wrap gap-1">
                                                {CONDITIONS.map((c) => {
                                                    const on = (l.acceptable_conditions || []).includes(c);
                                                    return (
                                                        <button
                                                            key={c}
                                                            type="button"
                                                            data-testid={`line-${i}-cond-${c}`}
                                                            onClick={() => {
                                                                const arr = l.acceptable_conditions || [];
                                                                const next = on ? arr.filter((x) => x !== c) : [...arr, c];
                                                                update(i, "acceptable_conditions", next);
                                                            }}
                                                            className={`px-2 py-1 font-display text-[11px] font-semibold tracking-[0.1em] border transition-colors ${
                                                                on
                                                                    ? "bg-[var(--ns-ink)] text-white border-[var(--ns-ink)]"
                                                                    : "bg-white border-[var(--ns-border)] hover:border-[var(--ns-ink)]"
                                                            }`}
                                                            style={!on ? { color: "var(--ns-body)" } : {}}
                                                        >
                                                            {c}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </td>
                                        <CellInput type="number" min={1} value={l.quantity} onChange={(v) => update(i, "quantity", v)} required testid={`line-${i}-qty`} />
                                        <CellInput value={l.requested_lead_time} onChange={(v) => update(i, "requested_lead_time", v)} placeholder="e.g. 30 days, AOG, ASAP" />
                                        <td className="px-4 py-3 text-center">
                                            <button
                                                type="button"
                                                onClick={() => removeLine(i)}
                                                disabled={lines.length === 1}
                                                className="inline-flex items-center justify-center w-8 h-8 text-[var(--ns-muted)] hover:text-[var(--ns-ink)] disabled:opacity-30"
                                                title="Remove line"
                                                data-testid={`line-${i}-remove`}
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="font-body text-sm" style={{ color: "var(--ns-muted)" }}>
                        {lines.length} line{lines.length === 1 ? "" : "s"} · A unique ticket number will be assigned automatically.
                    </div>
                    <button type="submit" disabled={busy} className="ns-btn-primary" data-testid="submit-rfq">
                        <Save size={15} />
                        {busy ? "Submitting…" : "Submit RFQ"}
                    </button>
                </div>
            </form>
        </div>
    );
};

const Th = ({ children, w }) => (
    <th className="text-left px-4 py-3.5 font-body text-[11px] font-semibold uppercase tracking-[0.16em]" style={{ color: "var(--ns-muted)", width: w }}>
        {children}
    </th>
);

const CellInput = ({ value, onChange, testid, ...rest }) => (
    <td className="px-3 py-2">
        <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testid}
            {...rest}
            className="w-full bg-white border border-[var(--ns-border)] px-3 py-2 font-body text-sm focus:outline-none focus:border-[var(--ns-ink)] transition-colors tabular-nums"
            style={{ color: "var(--ns-ink)" }}
        />
    </td>
);

export default NewRfq;
